import {
  DarkModeProvider,
  DirectionProvider,
  type LocaleType,
  SettingsProvider,
  ThemeProvider,
} from '@cloud/shared/ui'
import { useLocale, useLocaleDirection } from 'gt-react'
import type { ReactNode } from 'react'
import { ModulesProvider } from './modules-provider'

/**
 * App-wide provider stack. Mounted once in `__root.tsx` inside
 * QueryProvider + UserProvider + GTProvider so it persists across
 * route-group changes — moving between `/hr`, `/crm`, `/settings`,
 * `/dashboard`, `/m/:moduleKey`, etc. no longer remounts the whole
 * provider tree, which was causing a visible flash and dropping
 * the in-memory TanStack Query cache.
 *
 * Uses `useLocale()` / `useLocaleDirection()` to derive locale and
 * direction from GTProvider — callers don't pass them.
 */
export function Providers({ children }: { children: ReactNode }) {
  const locale = useLocale() as LocaleType
  const direction = useLocaleDirection(locale)

  return (
    <SettingsProvider locale={locale} settingsConfig={{ theme: false }}>
      <ModulesProvider>
        <DarkModeProvider>
          <ThemeProvider>
            <DirectionProvider direction={direction}>{children}</DirectionProvider>
          </ThemeProvider>
        </DarkModeProvider>
      </ModulesProvider>
    </SettingsProvider>
  )
}
